import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { FileBarChart } from "lucide-react";

import { disciplineService, type BehaviorCategory, type BehaviorPattern, type ClassDisciplinarySummary, type DisciplinaryReport, type StudentOption } from "../../services/disciplineService";
import { apiErrorMessage } from "../../utils/apiError";
import { exportAsCsv, exportAsExcel, exportAsPdf } from "../../utils/exportTable";
import { ExportButtons, type ExportFormat } from "../ExportButtons";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { SearchPick } from "../library/Pickers";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { humanise } from "../register/registerLogic";
import { REPORT_TYPES, classConfig, day, labelFrom, patternConfig, reportConfig, riskStatus, severityRows, shareAffected, when } from "./disciplineLogic";

const trendText: Record<string, string> = { improving: "Improving", stable: "Steady", worsening: "Getting worse" };

/** Each learner's behaviour by kind and month, from resolved cases. */
export function PatternsPanel({ patterns, isLoading, isError, onRetry, onPlan }: {
  patterns?: BehaviorPattern[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onPlan: () => void;
}) {
  const [viewing, setViewing] = useState<number | null>(null);
  const columns: RegisterColumn<BehaviorPattern>[] = [
    {
      header: "Learner",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.student_name}</span>
          {row.student_class && <span className="block text-xs text-gray-500">{row.student_class}</span>}
        </span>
      ),
      exportValue: (row) => row.student_name,
      className: "min-w-[10rem]",
    },
    { header: "Class", cell: (row) => row.student_class, exportValue: (row) => row.student_class, exportOnly: true },
    { header: "Behaviour", cell: (row) => row.category_name, exportValue: (row) => row.category_name, className: "text-gray-700" },
    { header: "Month", cell: (row) => day(row.analysis_start_date).slice(0, 7), exportValue: (row) => day(row.analysis_start_date).slice(0, 7), className: "tabular-nums text-gray-700" },
    { header: "Cases", cell: (row) => row.incident_count, exportValue: (row) => row.incident_count, align: "right", className: "tabular-nums" },
    { header: "Average severity", cell: (row) => Number(row.average_severity).toFixed(1), exportValue: (row) => Number(row.average_severity).toFixed(1), align: "right", className: "tabular-nums text-gray-700" },
    { header: "Trend", cell: (row) => <span className={row.trend_direction === "worsening" ? "text-red-700" : row.trend_direction === "improving" ? "text-emerald-700" : "text-gray-700"}>{trendText[row.trend_direction]}</span>, exportValue: (row) => trendText[row.trend_direction] },
    { header: "Risk", cell: (row) => <StatusDot {...riskStatus(row.risk_level)} />, exportValue: (row) => riskStatus(row.risk_level).label },
    { header: "Support", cell: (row) => (row.intervention_required ? <span className="font-medium text-amber-800">Recommended</span> : "—"), exportValue: (row) => (row.intervention_required ? "Recommended" : "") },
  ];
  const row = viewing !== null ? patterns?.find((item) => item.id === viewing) : undefined;
  return (
    <div className="space-y-4">
      <ClientRegister
        title="Behaviour patterns"
        description="Worked out from resolved cases, one row per learner, kind of behaviour and month. Support is recommended at high risk or three cases in a month."
        rows={patterns}
        config={patternConfig}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        noun={["pattern", "patterns"]}
        exportName={{ file: "behaviour-patterns", title: "Behaviour Patterns" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No patterns yet. They appear once cases are resolved."
      />
      {row && (
        <DetailsModal
          title={`${row.student_name}: ${row.category_name}`}
          headline={{ label: [row.student_class, row.student_id].filter(Boolean).join(" · "), value: `${row.incident_count} case${row.incident_count === 1 ? "" : "s"}`, note: `In ${day(row.analysis_start_date).slice(0, 7)}, worked out ${day(row.analysis_end_date)}` }}
          status={riskStatus(row.risk_level)}
          facts={[
            ["Average severity", Number(row.average_severity).toFixed(1)],
            ["Trend against the month before", trendText[row.trend_direction]],
            ["Support", row.intervention_required ? "Recommended" : "Not needed yet"],
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {row.intervention_required && (
                <button type="button" className="btn btn-primary" onClick={() => { setViewing(null); onPlan(); }}>Go to support plans</button>
              )}
            </>
          }
        />
      )}
    </div>
  );
}

/** Each class's cases, learners affected and plans at a glance. */
export function ClassesPanel({ classes, isLoading, isError, onRetry }: { classes?: ClassDisciplinarySummary[]; isLoading: boolean; isError: boolean; onRetry: () => void }) {
  const columns: RegisterColumn<ClassDisciplinarySummary>[] = [
    { header: "Class", cell: (row) => <span className="font-medium text-gray-900">{row.class_name}</span>, exportValue: (row) => row.class_name },
    { header: "Learners", cell: (row) => row.total_students, exportValue: (row) => row.total_students, align: "right", className: "tabular-nums text-gray-700" },
    { header: "With a case", cell: (row) => `${row.students_with_incidents} (${shareAffected(row)}%)`, exportValue: (row) => row.students_with_incidents, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Cases", cell: (row) => row.total_incidents, exportValue: (row) => row.total_incidents, align: "right", className: "tabular-nums" },
    { header: "This month", cell: (row) => row.incidents_this_month, exportValue: (row) => row.incidents_this_month, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Average severity", cell: (row) => (row.total_incidents ? Number(row.average_severity).toFixed(1) : "—"), exportValue: (row) => Number(row.average_severity).toFixed(1), align: "right", className: "tabular-nums text-gray-700" },
    { header: "Most common", cell: (row) => (row.most_common_category === "N/A" ? "—" : row.most_common_category), exportValue: (row) => (row.most_common_category === "N/A" ? "" : row.most_common_category), className: "text-gray-700" },
    { header: "Active plans", cell: (row) => row.active_interventions, exportValue: (row) => row.active_interventions, align: "right", className: "tabular-nums text-gray-700" },
  ];
  return (
    <ClientRegister
      title="By class"
      description="Cases and support plans in each class you can see."
      rows={classes}
      config={classConfig}
      columns={columns}
      rowKey={(row) => row.class_id}
      noun={["class", "classes"]}
      exportName={{ file: "conduct-by-class", title: "Conduct by Class" }}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      emptyText="No classes to show."
    />
  );
}

/** A report's figures, frozen when it was made. */
function ReportForm({ learners, categories, onClose, onDone }: { learners: StudentOption[]; categories: BehaviorCategory[]; onClose: () => void; onDone: (row: DisciplinaryReport) => void }) {
  const [learner, setLearner] = useState("");
  const [from, setFrom] = useState("");
  const save = useMutation({ mutationFn: disciplineService.createDisciplinaryReport, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (name: string) => String(data.get(name) ?? "").trim() || undefined;
    save.mutate({ name: text("name"), report_type: text("report_type"), student_filter: learner ? Number(learner) : undefined, category_filter: text("category_filter") ? Number(text("category_filter")) : undefined, date_from: from || undefined, date_to: text("date_to") });
  };
  return (
    <FormModal id="conduct-report" title="Make a report" busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The report could not be made.") : undefined} saveLabel="Make it" note="The figures are frozen as they are now." onClose={onClose} onSubmit={submit}>
      <FormSection title="The report" divided={false}>
        <Field label="Name" hint="e.g. Term 3 conduct, P6 September.">
          <input required name="name" maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Kind">
          <select name="report_type" defaultValue="comprehensive" className="input mt-1">
            {REPORT_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="From" optional>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input mt-1" />
        </Field>
        <Field label="To" optional>
          <input type="date" name="date_to" min={from || undefined} className="input mt-1" />
        </Field>
        <Field label="Kind of behaviour" optional>
          <select name="category_filter" className="input mt-1">
            <option value="">Every kind</option>
            {categories.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </Field>
        <Field label="One learner" optional as="div" className="sm:col-span-2">
          <SearchPick items={learners} value={learner} onChange={setLearner} label={(row) => row.full_name} detail={(row) => [row.class_name, row.student_id].filter(Boolean).join(" · ")} placeholder="Leave empty for every learner" empty="No learner matches" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Saved conduct reports: made by the committee and leadership. */
export function ReportsPanel({ reports, learners, categories, isLoading, isError, onRetry, onChanged }: {
  reports?: DisciplinaryReport[];
  learners: StudentOption[];
  categories: BehaviorCategory[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [making, setMaking] = useState(false);
  const scope = (row: DisciplinaryReport) =>
    [row.student_filter_name, row.category_filter_name, row.date_from || row.date_to ? `${day(row.date_from) || "start"} to ${day(row.date_to) || "now"}` : ""].filter(Boolean).join(" · ") || "Every case";
  const columns: RegisterColumn<DisciplinaryReport>[] = [
    { header: "Made", cell: (row) => day(row.generated_at), exportValue: (row) => when(row.generated_at), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Report", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "min-w-[12rem]" },
    { header: "Kind", cell: (row) => labelFrom(REPORT_TYPES, row.report_type), exportValue: (row) => labelFrom(REPORT_TYPES, row.report_type), className: "text-gray-700" },
    { header: "Covers", cell: (row) => scope(row), exportValue: (row) => scope(row), className: "text-gray-700" },
    { header: "Cases", cell: (row) => row.total_incidents, exportValue: (row) => row.total_incidents, align: "right", className: "tabular-nums" },
    { header: "Learners", cell: (row) => row.total_students, exportValue: (row) => row.total_students, align: "right", className: "tabular-nums" },
    { header: "Made by", cell: (row) => row.generated_by_name, exportValue: (row) => row.generated_by_name, className: "text-gray-700" },
  ];
  const report = viewing !== null ? reports?.find((row) => row.id === viewing) : undefined;
  const breakdown = report
    ? [
        ...severityRows(report.severity_breakdown).map((row) => ({ group: "Severity", label: row.label, count: row.count })),
        ...Object.entries(report.category_breakdown).map(([label, count]) => ({ group: "Kind of behaviour", label, count })),
        ...Object.entries((report.trend_data?.status_breakdown as Record<string, number>) ?? {}).map(([label, count]) => ({ group: "Status", label: humanise(label), count })),
      ]
    : [];
  const exportReport = (format: ExportFormat) => {
    if (!report) return;
    const columnsOut = [
      { header: "Group", accessor: (row: (typeof breakdown)[number]) => row.group },
      { header: "Item", accessor: (row: (typeof breakdown)[number]) => row.label },
      { header: "Cases", accessor: (row: (typeof breakdown)[number]) => row.count },
    ];
    const title = `${report.name} (${scope(report)})`;
    if (format === "csv") exportAsCsv(`conduct-report-${report.id}.csv`, columnsOut, breakdown);
    if (format === "excel") exportAsExcel(`conduct-report-${report.id}.xls`, title, columnsOut, breakdown);
    if (format === "pdf") exportAsPdf(title, columnsOut, breakdown);
  };

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Reports"
        description="Snapshots of the conduct record, frozen when made. Open one to read or download its breakdown."
        actions={<button type="button" onClick={() => setMaking(true)} className="btn-primary h-8 min-h-8 px-3 text-xs"><FileBarChart className="mr-1.5 h-3.5 w-3.5" /> Make a report</button>}
        rows={reports}
        config={reportConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        noun={["report", "reports"]}
        exportName={{ file: "conduct-reports", title: "Conduct Reports" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No reports made yet."
      />
      {report && (
        <DetailsModal
          size="3xl"
          title={report.name}
          headline={{ label: labelFrom(REPORT_TYPES, report.report_type), value: `${report.total_incidents} case${report.total_incidents === 1 ? "" : "s"}, ${report.total_students} learner${report.total_students === 1 ? "" : "s"}`, note: scope(report) }}
          facts={[["Made", when(report.generated_at)], ["Made by", report.generated_by_name]]}
          onClose={() => setViewing(null)}
        >
          <section className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Breakdown</h4>
              <ExportButtons onExport={exportReport} disabled={!breakdown.length} what="this report" />
            </div>
            <div className="sheet-wrap">
              <table className="sheet">
                <thead>
                  <tr>
                    <th>Group</th>
                    <th>Item</th>
                    <th className="text-right">Cases</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.map((row) => (
                    <tr key={`${row.group}-${row.label}`}>
                      <td className="text-gray-500">{row.group}</td>
                      <td className="text-gray-900">{row.label}</td>
                      <td className="text-right tabular-nums">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </DetailsModal>
      )}
      {making && (
        <ReportForm
          learners={learners}
          categories={categories}
          onClose={() => setMaking(false)}
          onDone={(saved) => {
            setMaking(false);
            setNotice({ tone: "success", text: `${saved.name} made: ${saved.total_incidents} cases.` });
            onChanged();
            setViewing(saved.id);
          }}
        />
      )}
    </div>
  );
}
